import Image from 'next/future/image';
import { useState, useContext } from 'react';
import { capitalize, uniqBy } from 'lodash-es';
import { SearchResultsOption } from '@/lib/search-helpers';
import useRouterTransition from '@/lib/use-router-transition';
import { QsContext } from '@/lib/use-qs';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import i18n from '@/settings/i18n';
import { ConditionPopup } from '@/components/products/conditionPopup/ConditionPopup';
import { isNaStore } from '@/lib/collections/catalogue/helpers';
import { generateFilterLabel } from '@/lib/collections/helpers';

const t = i18n.init();

export default function FilterGroup(props: {
  name: string;
  values?: Array<SearchResultsOption>;
  categories?: string[];
}) {
  const isTransitioning = useRouterTransition();
  const [isConditionOpen, setConditionIsOpen] = useState(false);
  const [show, setIsOpen] = useState(true);
  const [qs, updateQs] = useContext(QsContext);
  const { name, values, categories = [] } = props;

  if (values == null || values.length === 0) return null;

  return (
    <div className="border-b-2 bg-white p-5 sm:p-4">
      <ConditionPopup
        activeCondition={isNaStore ? t`Premium` : t`Brand New`}
        isOpen={isConditionOpen}
        onClose={() => setConditionIsOpen(false)}
        categoryPk="default"
      />
      <div className="flex items-start justify-between">
        <h2
          className="mb-4 cursor-pointer text-sm font-bold uppercase text-gray-700"
          onClick={() => setIsOpen(!show)}
        >
          <span
            className={` ${
              !show && `mb-1 border-t-0 border-b-gray-700`
            } relative top-[2px] mr-2.5 inline-block border-[5px] border-transparent border-t-gray-700`}
          ></span>
          {t(capitalize(name.replace(/([A-Z])/g, ' $1')))}
        </h2>
        {name === 'condition' && (
          <p
            className="inline-flex cursor-pointer items-center text-[10px] leading-5 text-gray-700 underline"
            onClick={() => setConditionIsOpen(true)}
          >
            <span className="hidden xs:inline">{t`Learn More`}</span>
            <span className="relative top-[-1px] ml-1 inline-flex cursor-pointer items-center">
              <Image
                src={infoIconBlack}
                alt={infoIconBlack}
                width={13}
                height={13}
                className="object-contain"
              />
            </span>
          </p>
        )}
      </div>
      <div
        className={`${
          show ? `max-h-56` : `max-h-0`
        } overflow-auto transition-all duration-700 ease-in-out`}
      >
        {uniqBy(
          values?.map((item) =>
            item.key === 'Very Good'
              ? { key: 'Good', count: item.count }
              : item,
          ),
          'key',
        )?.map(({ key, label }) => {
          const replacedKey = label || key.replace(/Oppo/g, 'OPPO');

          const filterLabel = generateFilterLabel(
            name,
            categories,
            replacedKey,
          );

          return (
            <label
              key={key}
              className="mb-2 block cursor-pointer text-sm text-gray-700 xl:text-base"
            >
              <input
                type="checkbox"
                disabled={isTransitioning}
                className="peer relative top-[2px] mr-2 h-[15px] w-[15px] accent-gray-700"
                checked={
                  name === 'categories'
                    ? qs[name]?.split('|').includes(key) &&
                      qs.categories?.split('|').length < 3
                    : qs[name]?.split('|').includes(key)
                }
                onChange={(e) => {
                  if (qs.categories?.split('|').length >= 3)
                    return updateQs({ [name]: `|${key}` });

                  if (e.target.checked)
                    return updateQs({ [name]: `${qs[name] || ''}|${key}` });
                  const nextQs = qs[name]
                    ?.split('|')
                    .filter((el: any) => el !== key)
                    .join('|');

                  return updateQs({ [name]: nextQs });
                }}
              />
              <span className="peer-disabled:opacity-70">{filterLabel}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
